import { Illustration, Text } from "@clipboard-health/ui-components";
import { Box, Stack } from "@mui/material";
import { APP_V2_APP_EVENTS } from "@src/appV2/lib";
import { logEvent } from "@src/appV2/lib/analytics";
import { useLogEffect } from "@src/appV2/lib/analytics/useLogEffect";
import { Button } from "@src/appV2/redesign/components/Button";
import { DialogFooter } from "@src/appV2/redesign/components/DialogFooter";
import { type PlacementCandidate } from "@src/appV2/redesign/PlacementCandidate/types";
import { type InterviewWithDetails } from "@src/appV2/redesign/WorkerShifts/api/useFetchPaginatedInterviews";

import { FEEDBACK_STEP, type FeedbackStep } from "./feedbackStep";
import { useInterviewAttendanceSource } from "./useInterviewAttendanceSource";

const WORKPLACE_OPTIONS = [
  // Using \u00A0 (non-breaking space) ensures space between text and emoji is preserved
  { label: "I received an offer\u00A0🎉", step: FEEDBACK_STEP.gotOfferSubOptions },
  { label: "They said they'd follow up later", step: FEEDBACK_STEP.notHeardBackSubOptions },
  { label: "It wasn\u2019t a match", step: FEEDBACK_STEP.notOfferTheRole },
] as const;

interface WorkplaceInterviewFeedbackContentProps {
  interview: InterviewWithDetails;
  onOptionSelected: (step: FeedbackStep) => void;
  isFetchingInterviews: boolean;
  placementCandidate: PlacementCandidate;
}

export function WorkplaceInterviewFeedbackContent(props: WorkplaceInterviewFeedbackContentProps) {
  const { interview, onOptionSelected, isFetchingInterviews, placementCandidate } = props;
  const source = useInterviewAttendanceSource();

  const eventContext = {
    placementCandidate,
    interview,
    source,
  };

  useLogEffect(APP_V2_APP_EVENTS.PLACEMENT_INTERVIEW_ATTENDANCE_WORKPLACE_VIEWED, eventContext);
  return (
    <>
      <Stack
        spacing={7}
        alignItems="center"
        justifyContent="center"
        sx={{ paddingX: 8, paddingY: 6 }}
      >
        <Illustration type="facility" />
        <Stack spacing={6} textAlign="center">
          <Text variant="h2">Did they share next steps?</Text>
          <Text>
            Let us know what they shared at{" "}
            <Box sx={{ fontWeight: 800 }} component="span">
              {interview.workplace?.name}
            </Box>
            , so nothing gets missed.
          </Text>
        </Stack>
      </Stack>
      <DialogFooter orientation="horizontal" sx={{ px: 8, pb: 8, pt: 2 }}>
        <Stack spacing={4} width="100%">
          {WORKPLACE_OPTIONS.map(({ label, step }) => (
            <Button
              key={step}
              variant="outlined"
              size="large"
              disabled={isFetchingInterviews}
              onClick={() => {
                logEvent(
                  APP_V2_APP_EVENTS.PLACEMENT_INTERVIEW_ATTENDANCE_WORKPLACE_OPTION_CLICKED,
                  {
                    ...eventContext,
                    option: step,
                  }
                );
                onOptionSelected(step);
              }}
            >
              {label}
            </Button>
          ))}
        </Stack>
      </DialogFooter>
    </>
  );
}
